import { create } from 'zustand';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useT } from '@/lib/i18n';

interface ConfirmState {
  open: boolean;
  message: string;
  confirmLabel?: string;
  resolve: ((ok: boolean) => void) | null;
  ask: (message: string, confirmLabel?: string) => Promise<boolean>;
  close: (ok: boolean) => void;
}

const useConfirmStore = create<ConfirmState>((set, get) => ({
  open: false,
  message: '',
  confirmLabel: undefined,
  resolve: null,
  ask: (message, confirmLabel) =>
    new Promise<boolean>(resolve => {
      // Resolve any in-flight prompt as cancelled before opening a new one.
      const prev = get().resolve;
      if (prev) prev(false);
      set({ open: true, message, confirmLabel, resolve });
    }),
  close: ok => {
    const { resolve } = get();
    if (resolve) resolve(ok);
    set({ open: false, message: '', confirmLabel: undefined, resolve: null });
  },
}));

/**
 * Promise-based confirmation replacing the native window.confirm(). Returns a
 * stable `confirm(message, confirmLabel?)` that resolves true on confirm, false
 * on cancel/dismiss. Requires <ConfirmDialog /> mounted once at the app root.
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm(t('common.delete') + ' ' + name + '?'))) return;
 */
export function useConfirm() {
  return useConfirmStore(s => s.ask);
}

/** Single host dialog — mount once near the app root (App.tsx). */
export function ConfirmDialog() {
  const t = useT();
  const open = useConfirmStore(s => s.open);
  const message = useConfirmStore(s => s.message);
  const confirmLabel = useConfirmStore(s => s.confirmLabel);
  const close = useConfirmStore(s => s.close);

  return (
    <Dialog open={open} onOpenChange={v => !v && close(false)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('common.confirm')}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            data-testid="confirm-ok"
            onClick={() => close(true)}
          >
            {confirmLabel ?? t('common.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
